import React from 'react';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

const PrivacyPage = () => {
    useSEO({
      title: 'Privacy Policy',
      description: 'How Veydra handles your data. What we collect, why we collect it, and your rights.'
    });

    return (
        <div className="min-h-screen bg-white">
            <TopNav />

            {/* Main Content */}
            <main className="max-w-3xl mx-auto px-8 py-16 md:py-24">
                    
                    {/* Title */}
                    <div className="mb-16">
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Privacy</p>
                        <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
                            How we handle your data
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            We believe in transparency. Here's what we collect, why we collect it, and what we'll never do.
                        </p>
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-12">
                        
                        {/* What We Collect */}
                        <section>
                            <h2 className="text-2xl font-medium text-gray-900 mb-4">What we collect</h2>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <div>
                                        <p className="text-gray-900 font-medium">Account information</p>
                                        <p className="text-gray-600">Name, email, and organization when you sign up.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <div>
                                        <p className="text-gray-900 font-medium">Usage data</p>
                                        <p className="text-gray-600">How you interact with the platform—which features you use, how often, and general patterns that help us improve.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <div>
                                        <p className="text-gray-900 font-medium">Model and simulation data</p>
                                        <p className="text-gray-600">The models you build and the data you upload to run simulations. This is your data.</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* How We Use It */}
                        <section>
                            <h2 className="text-2xl font-medium text-gray-900 mb-4">How we use it</h2>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">To provide and operate the Veydra platform</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">To improve our features and user experience</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">To communicate about your account, updates, or support requests</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">To develop new capabilities based on aggregate usage patterns</p>
                                </div>
                            </div>
                        </section>

                        {/* What We Don't Do */}
                        <section>
                            <h2 className="text-2xl font-medium text-gray-900 mb-4">What we don't do</h2>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                                <div className="space-y-3">
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-0.5">✓</span>
                                        <p className="text-gray-700">We <strong>never sell</strong> your data to third parties</p>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-0.5">✓</span>
                                        <p className="text-gray-700">We <strong>never share</strong> your models or simulation data with advertisers</p>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-0.5">✓</span>
                                        <p className="text-gray-700">We <strong>never use</strong> your proprietary data to train models for other customers</p>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-0.5">✓</span>
                                        <p className="text-gray-700">We <strong>never access</strong> your data except to provide support you've requested</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Your Rights */}
                        <section>
                            <h2 className="text-2xl font-medium text-gray-900 mb-4">Your rights</h2>
                            <p className="text-gray-700 mb-4">
                                You own your data. You can:
                            </p>
                            <div className="space-y-3">
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700"><strong>Export</strong> your models and data at any time</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700"><strong>Request deletion</strong> of your account and all associated data</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700"><strong>Update</strong> your personal information through your account settings</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700"><strong>Opt out</strong> of non-essential communications</p>
                                </div>
                            </div>
                        </section>

                        {/* Security */}
                        <section>
                            <h2 className="text-2xl font-medium text-gray-900 mb-4">Security</h2>
                            <p className="text-gray-700 mb-4">
                                We take security seriously. Your data is:
                            </p>
                            <div className="space-y-3">
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">Encrypted in transit and at rest</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">Hosted on secure cloud infrastructure (Google Cloud Platform)</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">Protected by industry-standard access controls</p>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-gray-400 mr-3 mt-1">—</span>
                                    <p className="text-gray-700">Backed up regularly with secure retention policies</p>
                                </div>
                            </div>
                        </section>

                        {/* Cookies */}
                        <section>
                            <h2 className="text-2xl font-medium text-gray-900 mb-4">Cookies</h2>
                            <p className="text-gray-700 mb-4">
                                We use cookies across veydra.io, app.veydra.io, docs.veydra.io, and our demo environments. 
                                When you first visit, a consent banner lets you choose which categories to allow. 
                                You can change your preferences at any time using the "Manage Cookies" link in the footer.
                            </p>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Essential cookies</h3>
                                    <p className="text-gray-700">
                                        Required for the platform to function. These handle authentication, security (including App Check), 
                                        and remembering your consent preferences. They cannot be disabled.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Analytics cookies</h3>
                                    <p className="text-gray-700">
                                        Help us understand how visitors use the site by collecting anonymous usage data through Google Analytics. 
                                        This data helps us improve the platform experience. These are only set if you consent.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Marketing cookies</h3>
                                    <p className="text-gray-700">
                                        Used to understand engagement patterns so we can provide relevant content and communications through Mautic. 
                                        These are only set if you consent.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Contact */}
                        <section>
                            <h2 className="text-2xl font-medium text-gray-900 mb-4">Questions?</h2>
                            <p className="text-gray-700 mb-4">
                                If you have questions about your privacy or want to exercise any of your rights, 
                                contact us at <a href="mailto:support@veydra.io" className="text-gray-900 underline hover:no-underline">support@veydra.io</a>.
                            </p>
                        </section>

                        {/* Legal Terms Link */}
                        <section className="pt-8 border-t border-gray-200">
                            <p className="text-gray-600">
                                For the complete legal terms governing your use of Veydra, see our{' '}
                                <a href="https://app.veydra.io/terms" className="text-gray-900 underline hover:no-underline">Terms of Service</a>.
                            </p>
                            <p className="text-sm text-gray-500 mt-4">
                                Last updated: March 2026
                            </p>
                        </section>

                    </div>
            </main>

            <Footer />
        </div>
    );
};

export default PrivacyPage;
